import React from "react";
import { connect } from "react-redux";
import { Container, Card, CardBody, Row, Col, Nav, NavItem, NavLink, Progress } from "reactstrap";
import classnames from "classnames";
import api from "./../../../Api.js";
import { formatXp } from "./../../../helper/xpHelpers.js";

import CharacterRpg from "./CharacterRpg.jsx";
import ProgressHistory from "./ProgressHistory.jsx";
import AiAtelier from "./AiAtelier.jsx";
import Missions from "./Missions.jsx";
import GlobalLeaderboard from "./GlobalLeaderboard.jsx";
import Achievements from "./../badge/Achievements.jsx";
import GoldShop from "./../rpg/GoldShop.jsx";
import RpgLobby from "./../rpg/RpgLobby.jsx";
import DmLobby from "./../rpg/DmLobby.jsx";
import HpBar from "./../rpg/HpBar.jsx";
import GoldDisplay from "./../rpg/GoldDisplay.jsx";

/**
 * GameHub — modulo unico de gamificacao (rota /game/:tab?).
 *
 * Junta todas as telas do RPG num so lugar, com abas, pra nao inflar o
 * menu lateral. A faixa do jogador (HP/level/XP/gold) fica sempre visivel
 * no topo e recarrega a cada troca de aba (saldo muda na loja/estilista).
 */

var TABS = [
  { key: "personagem", label: "Personagem", icon: "fas fa-user-astronaut", component: CharacterRpg },
  { key: "missoes", label: "Missões", icon: "fas fa-flag-checkered", component: Missions },
  { key: "diario", label: "Diário", icon: "fas fa-scroll", component: ProgressHistory },
  { key: "estilista", label: "Estilista IA", icon: "fas fa-magic", component: AiAtelier },
  { key: "lojinha", label: "Lojinha", icon: "fas fa-store", component: GoldShop },
  { key: "ranking", label: "Ranking", icon: "fas fa-trophy", component: GlobalLeaderboard },
  { key: "conquistas", label: "Conquistas", icon: "fas fa-award", component: Achievements },
  { key: "arena", label: "Aula ao Vivo", icon: "fas fa-bolt", component: RpgLobby },
  { key: "mestre", label: "Mestre do Jogo", icon: "fas fa-dragon", component: DmLobby, teacherOnly: true }
];

class GameHub extends React.Component {

  constructor(props) {
    super(props);
    this.state = { status: null };
  }

  async componentDidMount() {
    await this.loadStatus();
  }

  componentDidUpdate(prevProps) {
    var prevTab = prevProps.match && prevProps.match.params ? prevProps.match.params.tab : null;
    var tab = this.props.match && this.props.match.params ? this.props.match.params.tab : null;
    if (prevTab !== tab) this.loadStatus();
  }

  async loadStatus() {
    if (!this.props.user || !this.props.user.id) return;
    try {
      var res = await api.getRpgStatus(this.props.user.id);
      this.setState({ status: res.data });
    } catch (e) {}
  }

  isTeacher() {
    var perms = this.props.permission || [];
    return perms.indexOf(21) !== -1 || perms.indexOf(24) !== -1;
  }

  activeTab() {
    var tab = this.props.match && this.props.match.params ? this.props.match.params.tab : null;
    var found = TABS.find((t) => t.key === tab);
    if (found && (!found.teacherOnly || this.isTeacher())) return found;
    return TABS[0];
  }

  goTo(key) {
    this.props.history.push("/game/" + key);
  }

  //identidade do jogador dentro do banner do tema — mesmo mecanismo do SimpleHeader:
  //a aparencia da instalacao define o degrade (csplus = vermelho, portal = degrade)
  renderHeader() {
    var status = this.state.status || {};
    var xp = status.xp || null;
    var gold = (status.gold && status.gold.balance) || 0;
    var level = xp && xp.currentLevel ? xp.currentLevel : { level: 1, name: "...", color: "#CD7F32", icon: "fas fa-medal" };

    var confs = (window.app && window.app.state && window.app.state.confs) || {};
    var themed = confs.headerDegrade1 && confs.headerDegrade2;
    var headerStyle = themed
      ? { background: "linear-gradient(-45deg," + confs.headerDegrade1 + "," + confs.headerDegrade2 + ")" }
      : undefined;

    return (
      <div className={"header pb-6 pt-5 pt-md-7" + (themed ? "" : " bg-gradient-info")} style={headerStyle}>
        <Container fluid>
          <div className="header-body d-flex align-items-center justify-content-between flex-wrap">
            <div className="d-flex align-items-center">
              <div style={{
                width: "56px", height: "56px", borderRadius: "50%",
                background: "rgba(255,255,255,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "3px solid rgba(255,255,255,0.3)", marginRight: "14px", flexShrink: 0
              }}>
                <i className={level.icon || "fas fa-medal"} style={{ fontSize: "1.4rem", color: "#fff" }} />
              </div>
              <div>
                <h1 className="text-white mb-0">
                  <i className="fas fa-gamepad mr-2" />
                  {(this.props.user && this.props.user.name) || "Aventura"}
                </h1>
                <p className="text-white-50 mb-0">
                  Nível {level.level} · {level.name}
                  {xp ? " · " + formatXp(xp.totalXp) + " XP" : ""}
                </p>
              </div>
            </div>
            <div className="mt-2 mt-md-0">
              <GoldDisplay balance={gold} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  render() {
    var active = this.activeTab();
    var TabComponent = active.component;
    var visibleTabs = TABS.filter((t) => !t.teacherOnly || this.isTeacher());
    var status = this.state.status || {};
    var hp = status.hp || { currentHp: 100, maxHp: 100 };
    var xp = status.xp || null;
    var pct = xp && xp.percentToNext !== undefined ? xp.percentToNext : 0;

    return (
      <React.Fragment>
        {this.renderHeader()}

        <Container className="mt--6" fluid>
          {/* card branco sobreposto ao banner, no padrao do app: vida + XP + abas */}
          <Card className="shadow mb-4">
            <CardBody className="py-3">
              <Row className="align-items-center mb-3">
                <Col md="6" className="mb-2 mb-md-0">
                  <HpBar currentHp={hp.currentHp} maxHp={hp.maxHp} compact />
                </Col>
                <Col md="6">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span style={{ fontSize: "0.7rem", fontWeight: 600 }}>
                      <i className="fas fa-bolt mr-1 text-yellow" />XP
                    </span>
                    <span className="text-yellow" style={{ fontSize: "0.7rem", fontWeight: 700 }}>
                      {xp ? formatXp(xp.totalXp) + " · falta " + formatXp(xp.xpToNextLevel || 0) : "..."}
                    </span>
                  </div>
                  <Progress
                    value={pct}
                    style={{ height: "8px", backgroundColor: "#f0f0f0" }}
                    barStyle={{ background: "linear-gradient(90deg, #FFD700 0%, #FFA500 100%)" }}
                  />
                </Col>
              </Row>

              <Nav pills className="flex-wrap" style={{ gap: "4px" }}>
                {visibleTabs.map((t) => (
                  <NavItem key={t.key}>
                    <NavLink
                      href="#"
                      className={classnames("py-2 px-3", { active: active.key === t.key })}
                      onClick={(e) => { e.preventDefault(); this.goTo(t.key); }}
                      style={{ cursor: "pointer", fontWeight: 600, fontSize: "0.85rem" }}
                    >
                      <i className={t.icon + " mr-1"} />
                      {t.label}
                      {t.key === "mestre" && " 🐉"}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </CardBody>
          </Card>
        </Container>

        {/* a aba renderiza a pagina inteira em modo embedded (sem header proprio) */}
        <TabComponent key={active.key} embedded={true} history={this.props.history} location={this.props.location} match={this.props.match} />
      </React.Fragment>
    );
  }
}

export default connect((state) => ({ user: state.user, permission: state.permission }))(GameHub);
